import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconArrowRight, IconInfoCircleFilled } from '@tabler/icons-react';
import { twMerge } from 'tailwind-merge';
import { FloatingPanel } from './FloatingPanel';
import type { HelpTip } from '@/docs/tips/types';
import { useDocPageVisible } from '@/docs/useDocPageVisible';
import { useUserRole } from '@/hooks/auth.hooks';

interface InfoTipProps {
  /** Bilingual tip (see src/docs/tips). Takes precedence over title / text / docSlug. */
  tip?: HelpTip;
  /** Short title of the explanation. */
  title?: string | React.ReactNode;
  /** Explanation (a full paragraph can be provided). */
  text?: string | React.ReactNode;
  /** Documentation page to open for more details. */
  docSlug?: string;
  size?: number;
  className?: string;
  width?: number;
}

/**
 * "i" icon: explains a precise piece of information (a figure, a column,
 * a block...) in a small panel displayed on hover or click.
 */
export const InfoTip = ({
  tip,
  title: titleProp,
  text: textProp,
  docSlug: docSlugProp,
  size = 15,
  className,
  width = 320,
}: InfoTipProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const title = tip ? tip.title[lang] : titleProp;
  const text = tip ? tip.text[lang] : textProp;
  const docSlug = tip ? tip.docSlug : docSlugProp;
  // The link is only displayed when the user's role gives access to the page
  // (users without a role can't open the documentation)
  const docVisible = useDocPageVisible(docSlug);
  const { hasNoRole } = useUserRole();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>();

  const open = hovered || pinned;

  const onEnter = () => {
    clearTimeout(closeTimeout.current);
    setHovered(true);
  };

  const onLeave = () => {
    closeTimeout.current = setTimeout(() => setHovered(false), 150);
  };

  useEffect(() => {
    if (!pinned) return undefined;
    const onClickOutside = (e: MouseEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)) {
        const panel = document.querySelector('[data-infotip-panel="true"]');
        if (!panel?.contains(e.target as Node)) setPinned(false);
      }
    };
    const onEscape = (e: KeyboardEvent) =>
      e.key === 'Escape' && setPinned(false);
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [pinned]);

  useEffect(() => () => clearTimeout(closeTimeout.current), []);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-label={t('common:moreInformation')}
        aria-expanded={open}
        className={twMerge(
          'inline-flex shrink-0 items-center justify-center align-middle rounded-full text-primary transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-5433FF/40 dark-mode:text-gray-800 dark-mode:hover:text-primary',
          // open && 'text-primary dark-mode:text-primary',
          className,
        )}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={onEnter}
        onBlur={onLeave}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setPinned((prev) => !prev);
        }}
      >
        <i
          className="ki-outline ki-information-2 leading-none"
          style={{ fontSize: size }}
        />
      </button>
      <FloatingPanel
        anchorRef={anchorRef}
        open={open}
        width={width}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <div
          data-infotip-panel="true"
          className="p-4 flex flex-col gap-2 text-left"
        >
          {title && (
            <div className="flex items-center gap-2 text-sm font-semibold text-black-071336 dark-mode:text-gray-900">
              <IconInfoCircleFilled
                size={16}
                className="text-purple-5433FF shrink-0"
              />
              {title}
            </div>
          )}
          <div className="text-[13px] leading-relaxed text-gray-700 font-normal normal-case whitespace-normal dark-mode:text-gray-700">
            {text}
          </div>
          {docSlug && docVisible && !hasNoRole && (
            <Link
              to={`/documentation/${docSlug}`}
              className="mt-1 inline-flex items-center gap-1 self-start text-xs font-medium text-purple-5433FF hover:underline"
              onClick={() => {
                setPinned(false);
                setHovered(false);
              }}
            >
              {t('common:learnMoreInDocumentation')}
              <IconArrowRight size={13} />
            </Link>
          )}
        </div>
      </FloatingPanel>
    </>
  );
};
